import { useState, useEffect } from "react";
import { Gift, Copy, Check, Users, TrendingUp, Share2, Link } from "lucide-react";
import { useAuth, BACKEND_URL } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import AuthModal from "../components/AuthModal";

const AffiliatePage = () => {
  const { user, authToken } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    if (user?.user_id) {
      fetch(`${BACKEND_URL}/api/referral/stats?user_id=${user.user_id}`)
        .then(r => r.json())
        .then(data => { setReferralData(data); setLoading(false); })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const referralLink = referralData?.referral_code
    ? `${window.location.origin}?ref=${referralData.referral_code}`
    : "";

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({
        title: "Join DJsandMCMedia",
        text: "Launch your own radio channel on DJsandMCMedia — The Shopify for Radio Creators!",
        url: referralLink,
      }).catch(() => {});
    } else {
      copyLink();
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO title="Affiliate Program" description="Invite creators to DJsandMCMedia and earn 1 free month for every signup." />
      <Header />

      <section className="pt-28 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/20 via-[#0a0a0a] to-[#0a0a0a]" />

        {/* Floating Dollar Signs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { x: '5%', y: '8%', size: 38, delay: 0, dur: 8, opacity: 0.30 },
            { x: '15%', y: '55%', size: 30, delay: 2.5, dur: 10, opacity: 0.20 },
            { x: '25%', y: '20%', size: 44, delay: 1.0, dur: 9, opacity: 0.25 },
            { x: '38%', y: '70%', size: 28, delay: 4.0, dur: 11, opacity: 0.18 },
            { x: '50%', y: '12%', size: 36, delay: 0.5, dur: 7, opacity: 0.28 },
            { x: '62%', y: '60%', size: 40, delay: 3.2, dur: 9, opacity: 0.22 },
            { x: '72%', y: '25%', size: 32, delay: 1.8, dur: 10, opacity: 0.26 },
            { x: '82%', y: '75%', size: 42, delay: 5.0, dur: 8, opacity: 0.20 },
            { x: '92%', y: '15%', size: 30, delay: 2.0, dur: 11, opacity: 0.24 },
            { x: '10%', y: '85%', size: 34, delay: 3.8, dur: 9, opacity: 0.18 },
            { x: '45%', y: '42%', size: 28, delay: 6.0, dur: 12, opacity: 0.15 },
            { x: '78%', y: '48%', size: 38, delay: 0.8, dur: 8, opacity: 0.22 },
            { x: '33%', y: '90%', size: 32, delay: 4.5, dur: 10, opacity: 0.20 },
            { x: '88%', y: '38%', size: 26, delay: 7.0, dur: 9, opacity: 0.16 },
            { x: '58%', y: '88%', size: 40, delay: 1.5, dur: 11, opacity: 0.24 },
            { x: '2%', y: '40%', size: 34, delay: 5.5, dur: 7, opacity: 0.22 },
            { x: '68%', y: '5%', size: 30, delay: 2.8, dur: 10, opacity: 0.20 },
            { x: '42%', y: '30%', size: 46, delay: 0.3, dur: 14, opacity: 0.16 },
          ].map((d, i) => (
            <svg key={i} className="absolute"
              style={{
                left: d.x, top: d.y,
                width: `${d.size}px`, height: `${d.size}px`,
                opacity: d.opacity,
                animation: `dollarFloat${i % 3} ${d.dur}s ease-in-out ${d.delay}s infinite`,
                filter: 'drop-shadow(0 0 8px rgba(34, 197, 94, 0.5))',
              }}
              viewBox="0 0 24 24" fill="none">
              <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
                fill="#4ade80" fontSize="20" fontWeight="bold" fontFamily="system-ui">$</text>
            </svg>
          ))}
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">

        <style>{`
          @keyframes dollarFloat0 {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            33% { transform: translateY(-12px) rotate(5deg); }
            66% { transform: translateY(6px) rotate(-3deg); }
          }
          @keyframes dollarFloat1 {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            25% { transform: translateY(8px) rotate(-6deg); }
            75% { transform: translateY(-10px) rotate(4deg); }
          }
          @keyframes dollarFloat2 {
            0%, 100% { transform: translateY(0) rotate(0deg) scale(1); }
            50% { transform: translateY(-14px) rotate(8deg) scale(1.05); }
          }
        `}</style>
          <div className="w-20 h-20 bg-emerald-500/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <Gift className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="affiliate-heading">
            Invite Creators. Get Rewarded.
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto mb-4">
            Share your unique referral link. For every creator who signs up, you earn <strong className="text-emerald-400">1 free month</strong> of your plan.
          </p>
          <p className="text-white/40 text-sm">No limits. The more you share, the more you earn.</p>
        </div>
      </section>

      {/* How It Works */}
      <section className="pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="relative bg-white/5 border border-emerald-500/10 rounded-2xl p-6 text-center shadow-lg shadow-emerald-500/5" data-testid="affiliate-step-1">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-emerald-500/10 to-transparent pointer-events-none" />
              <div className="relative">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Link className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">1. Share Your Link</h3>
                <p className="text-white/50 text-sm">Copy your unique referral link and share it anywhere — social media, email, your website.</p>
              </div>
            </div>
            <div className="relative bg-white/5 border border-emerald-500/10 rounded-2xl p-6 text-center shadow-lg shadow-emerald-500/5" data-testid="affiliate-step-2">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-emerald-500/10 to-transparent pointer-events-none" />
              <div className="relative">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">2. Friends Sign Up</h3>
                <p className="text-white/50 text-sm">When someone signs up using your link, they're tagged as your referral automatically.</p>
              </div>
            </div>
            <div className="relative bg-white/5 border border-emerald-500/10 rounded-2xl p-6 text-center shadow-lg shadow-emerald-500/5" data-testid="affiliate-step-3">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-emerald-500/10 to-transparent pointer-events-none" />
              <div className="relative">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">3. Earn Free Months</h3>
                <p className="text-white/50 text-sm">For each successful referral, you get 1 month free on your current plan. Stack them up!</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Referral Dashboard */}
      <section className="pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          {!user ? (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center" data-testid="affiliate-login-prompt">
              <Gift className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3">Log in to get your referral link</h3>
              <p className="text-white/50 mb-6">Sign in or create an account to start earning free months.</p>
              <button onClick={() => setShowAuth(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-xl transition font-semibold inline-block" data-testid="affiliate-get-started">
                Get Started
              </button>
              <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
            </div>
          ) : loading ? (
            <div className="text-center text-white/40 py-12">Loading your referral data...</div>
          ) : (
            <div className="space-y-6">
              {/* Referral Link */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="affiliate-link-section">
                <h3 className="text-lg font-bold mb-4">Your Referral Link</h3>
                <div className="flex gap-2 mb-4">
                  <div className="flex-1 bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-emerald-400 font-mono text-sm truncate" data-testid="affiliate-link">
                    {referralLink}
                  </div>
                  <button onClick={copyLink}
                    className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 px-5 py-3 rounded-xl transition flex items-center gap-2 text-sm font-medium flex-shrink-0"
                    data-testid="affiliate-copy-btn">
                    {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy</>}
                  </button>
                  <button onClick={shareLink}
                    className="bg-white/5 hover:bg-white/10 text-white/70 px-5 py-3 rounded-xl transition flex items-center gap-2 text-sm font-medium flex-shrink-0"
                    data-testid="affiliate-share-btn">
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>
                <p className="text-white/30 text-xs">Your code: <span className="text-white/60 font-mono">{referralData?.referral_code}</span></p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4" data-testid="affiliate-stats">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                  <Users className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white">{referralData?.referral_count || 0}</p>
                  <p className="text-white/40 text-sm">Creators Referred</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                  <Gift className="w-8 h-8 text-green-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-green-400">{referralData?.reward_months || 0}</p>
                  <p className="text-white/40 text-sm">Free Months Earned</p>
                </div>
              </div>

              {/* Referred Users List */}
              {referralData?.referred_users?.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="affiliate-referred-users">
                  <div className="p-4 border-b border-white/10">
                    <h3 className="text-lg font-bold">Your Referrals</h3>
                  </div>
                  <div className="divide-y divide-white/5">
                    {referralData.referred_users.map((u, i) => (
                      <div key={i} className="p-4 flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium text-sm">{u.name}</p>
                          <p className="text-white/40 text-xs">{u.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-white/50 text-xs capitalize">{u.plan} plan</p>
                          {u.created_at && <p className="text-white/30 text-xs">{new Date(u.created_at).toLocaleDateString()}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Share Ideas */}
              <div className="bg-gradient-to-b from-emerald-500/5 to-transparent border border-emerald-500/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold mb-3">Sharing Ideas</h3>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">•</span> Post your link on Twitter/X with a short review of the platform</li>
                  <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">•</span> Add it to your DJ bio or Linktree</li>
                  <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">•</span> Share in Discord/Facebook groups for DJs and music creators</li>
                  <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">•</span> Tell other DJs at gigs or events — word of mouth works</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AffiliatePage;
